import React, { useState, useEffect, useRef } from 'react';

import CommonImage from '@components/CommonImage';

import { getInputValue, formatNumericValue, parseInputValue } from '../utils';

import DisplayCost from './DisplayCost';

const PrintTable = ({
	data = [],
	totalCost = 0,
	onChange,
	isEditable = true,
	validationErrors = {},
	onFieldChange
}) => {
	const [prints, setPrints] = useState([]);
	const isInitialMount = useRef(true);
	const prevDataRef = useRef(null);

	useEffect(() => {
		const dataString = JSON.stringify(data);
		const prevDataString = JSON.stringify(prevDataRef.current);

		if (dataString === prevDataString) {
			return;
		}

		if (data && Array.isArray(data) && data.length > 0) {
			const newPrints = data.map((item, index) => ({
				id: `print-${index}`,
				type: item.selected_print_type || '',
				numberOfColors: item.selected_no_of_colors || '',
				coverage: item.selected_coverage || '',
				areaSize: item.selected_area || '',
				cost: formatNumericValue(item.print_price_usd),
				washPrice: formatNumericValue(item.wash_price_usd),
				providedInputArea: item.provided_input_area || '',
				providedInputCoverage: item.provided_input_coverage || ''
			}));
			setPrints(newPrints);
		} else {
			setPrints([
				{
					id: 'print-0',
					type: '',
					numberOfColors: '',
					coverage: '',
					areaSize: '',
					cost: '',
					washPrice: '',
					providedInputArea: '',
					providedInputCoverage: ''
				}
			]);
		}

		prevDataRef.current = data;
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, [data]);

	const transformToApiFormat = (updatedPrints) => {
		return updatedPrints.map((print) => {
			const { type, numberOfColors, coverage, areaSize, cost, id, print_changed } = print;

			return {
				print_price_usd: formatNumericValue(cost),
				selected_area: areaSize || '',
				selected_coverage: coverage || '',
				selected_print_type: type || '',
				selected_no_of_colors: numberOfColors || '',
				id,
				print_changed
			};
		});
	};

	const notifyChange = (updatedPrints) => {
		if (onChange && !isInitialMount.current) {
			onChange(transformToApiFormat(updatedPrints));
		}
	};

	const handleAddPrint = () => {
		const newPrint = {
			id: `print-${Date.now()}`,
			type: '',
			numberOfColors: '',
			coverage: '',
			areaSize: '',
			cost: '',
			washPrice: '',
			providedInputArea: '',
			providedInputCoverage: ''
		};
		const updatedPrints = [...prints, newPrint];
		setPrints(updatedPrints);
		notifyChange(updatedPrints);
	};

	const handleRemovePrint = (id) => {
		if (prints.length > 1) {
			const updatedPrints = prints.filter((p) => p.id !== id);
			setPrints(updatedPrints);
			notifyChange(updatedPrints);
		}
	};

	const handleInputChange = (printId, field, value) => {
		const textFields = ['type', 'areaSize', 'coverage', 'numberOfColors'];
		const isTextField = textFields.includes(field);

		const updatedValue = parseInputValue(value, !isTextField);
		if (onFieldChange) onFieldChange(`print_${field}`);
		const updatedPrints = prints.map((p) =>
			p.id === printId ? { ...p, [field]: updatedValue, print_changed: isTextField } : p
		);
		setPrints(updatedPrints);
		notifyChange(updatedPrints);
	};

	return (
		<div className="single-table costing-table-content mt-5">
			<div className="costing-table scroll-x-label">
				<table className="table no-hover">
					<thead>
						<tr>
							<th>Print Cost Metrics</th>
							{prints.map((print, index) => (
								<th key={print.id}>
									<div className="d-flex align-items-center justify-content-between">
										<p>Print {index + 1}</p>
										{prints.length > 1 && isEditable && (
											<CommonImage
												src="/icons/chip-close.svg"
												alt="remove"
												width={20}
												height={20}
												className="cursor-pointer"
												onClick={() => handleRemovePrint(print.id)}
											/>
										)}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Type</p>
							</td>
							{prints.map((print, index) => {
								const errorKey = `print-${index}-type`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={print.id}>
										<input
											type="text"
											value={print.type || ''}
											onChange={(e) =>
												handleInputChange(print.id, 'type', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Number of Colors</p>
							</td>
							{prints.map((print, index) => {
								const errorKey = `print-${index}-numberOfColors`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={print.id}>
										<input
											type="number"
											value={getInputValue(print.numberOfColors)}
											onChange={(e) =>
												handleInputChange(print.id, 'numberOfColors', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Coverage (%)</p>
							</td>
							{prints.map((print, index) => {
								const errorKey = `print-${index}-coverage`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={print.id}>
										<input
											type="number"
											step="0.01"
											value={getInputValue(print.coverage)}
											onChange={(e) =>
												handleInputChange(print.id, 'coverage', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Area Size</p>
							</td>
							{prints.map((print, index) => {
								const errorKey = `print-${index}-areaSize`;
								const hasError = validationErrors[errorKey];
								return (
									<td key={print.id}>
										<input
											type="text"
											value={print.areaSize || ''}
											onChange={(e) =>
												handleInputChange(print.id, 'areaSize', e.target.value)
											}
											readOnly={!isEditable}
											className={hasError ? 'common-border-error' : ''}
										/>
									</td>
								);
							})}
						</tr>
						<tr>
							<td>
								<p className="regular-12 gray_dark_02">Cost ($)</p>
							</td>
							{prints.map((print) => (
								<td key={print.id}>
									<input
										type="number"
										step="0.01"
										value={getInputValue(print.cost)}
										onChange={(e) => handleInputChange(print.id, 'cost', e.target.value)}
										readOnly={!isEditable}
									/>
								</td>
							))}
						</tr>
						<tr className="cost-total-row">
							<td>
								<p className="semibold-14 gray_dark_01">Total Print Cost</p>
							</td>
							<td colSpan={prints.length}>
								<p className="semibold-14 gray_dark_01">
									<DisplayCost totalCost={totalCost} />
								</p>
							</td>
						</tr>
					</tbody>
				</table>

				{isEditable && (
					<div className="add-more-button w-100">
						<button className="button gray-outline size32 w-100" onClick={handleAddPrint}>
							<CommonImage src="/icons/plus-20.svg" alt="add" width={20} height={20} />
							<span className="ml-1 regular-14 gray_dark_01">Add Print</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default PrintTable;
